/**
 * Enrichment backlog + extraction-run status (tiered-extraction decision, 2026-07-06).
 *
 * Three extraction tiers share ONE goal (the knowledge graph) at different token
 * intensities: full conversation mining (LLM over normalized transcripts, opt-in),
 * worklog mining (LLM over worklogs — the ~1/20-cost middle tier), and curated
 * extraction (deterministic, free, always on). The DEFAULT is curated-only; worklog
 * mining is PROPOSED by the agent at session start ("mine the recent week?"), and a
 * dashboard can drive per-item extraction. Both need cheap, deterministic answers to:
 *
 *   1. Which records still need LLM enrichment, per category?   → enrichmentStatus()
 *   2. How many transcripts were never conversation-mined?      → conversationBacklog()
 *   3. Is an extraction batch running RIGHT NOW?                → read/write/clearExtractionRun()
 *
 * The run marker is a small JSON file under `_indexes/` written by whoever drives a
 * batch (a dashboard runner, an agent). It is advisory, not a lock: readers treat a
 * stale heartbeat (default 10 min) or a dead pid as "not running", so a crashed
 * runner can never wedge the session-report proposal or a dashboard button forever.
 * Writers must re-write the marker (bumping `updatedAt`) as items complete.
 */

import { existsSync, mkdirSync, readdirSync, readFileSync, renameSync, rmSync, statSync, writeFileSync } from "node:fs";
import { basename, join } from "node:path";
import type { OntosStore } from "../sqlite/store.js";
import type { RecordInput } from "../extract/types.js";
import { parseFrontmatter } from "../internal/frontmatter.js";
import { needsEnrichment } from "./index.js";

/** The three prose-heavy source categories the LLM gap-filler covers (mirrors enrich-ontos). */
export type EnrichableCategory = "worklog" | "decision" | "memory";

export interface EnrichmentSource {
  readonly dir: string;
  readonly category: EnrichableCategory;
  readonly recursive: boolean;
}

/** Canonical enrichable-source layout for a data dir (single source of truth for the CLI too). */
export function enrichmentSources(dataDir: string): EnrichmentSource[] {
  return [
    { dir: join(dataDir, "worklog"), category: "worklog", recursive: true },
    { dir: join(dataDir, "decision-log"), category: "decision", recursive: false },
    { dir: join(dataDir, "_memory"), category: "memory", recursive: false },
  ];
}

function listMd(dir: string, recursive: boolean): string[] {
  let entries;
  try {
    entries = readdirSync(dir, { withFileTypes: true });
  } catch {
    return []; // dir absent — skip
  }
  const out: string[] = [];
  for (const e of entries) {
    if (e.isDirectory()) {
      if (recursive) out.push(...listMd(join(dir, e.name), true));
    } else if (e.isFile() && e.name.endsWith(".md") && e.name !== "_INDEX.md") {
      out.push(join(dir, e.name));
    }
  }
  return out;
}

/** Enrichable records under a data dir, optionally restricted to some categories. */
export function listEnrichableRecords(
  dataDir: string,
  categories?: readonly EnrichableCategory[],
): { path: string; category: EnrichableCategory }[] {
  const want = categories && categories.length > 0 ? new Set(categories) : null;
  const out: { path: string; category: EnrichableCategory }[] = [];
  for (const src of enrichmentSources(dataDir)) {
    if (want && !want.has(src.category)) continue;
    for (const path of listMd(src.dir, src.recursive)) out.push({ path, category: src.category });
  }
  return out;
}

// Real calendar dates only (month 01-12, day 01-31) — a malformed "2026-99-99"
// must not parse and then sort as "newest" (Codex R2 #2).
const DATE_RE = /(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])/;

/** Today as "YYYY-MM-DD" in LOCAL time — record dates are local calendar dates
 *  (worklog filenames), so a UTC "today" would shift the recent window by a day
 *  for users east of UTC every evening (Codex R2 #1). */
export function localToday(now = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * Best-effort record date as "YYYY-MM-DD": frontmatter `created`/`date` first,
 * then a date in the FILENAME (worklog/decision naming — basename only, so a
 * dated parent directory like `…\2026-01-01\…` can't be mistaken for the record
 * date; Codex R1 #5), then fs mtime. Used for "recent N days" scoping —
 * approximate by design, never load-bearing for safety.
 */
export function recordDate(path: string, frontmatter?: Record<string, unknown>): string | null {
  for (const key of ["created", "date"]) {
    const v = frontmatter?.[key];
    const m = v == null ? null : String(v instanceof Date ? v.toISOString() : v).match(DATE_RE);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const fm = basename(path).match(DATE_RE);
  if (fm) return `${fm[1]}-${fm[2]}-${fm[3]}`;
  try {
    return statSync(path).mtime.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

export interface CategoryBacklog {
  total: number;
  /** Records whose current text already has a matching enrichment stamp. */
  enriched: number;
  pending: number;
  /** Pending records dated within the last `recentDays` days (inclusive). */
  pendingRecent: number;
  /** Pending records older than the recent window (or undatable). */
  pendingOlder: number;
}

export interface EnrichmentStatus {
  readonly categories: Partial<Record<EnrichableCategory, CategoryBacklog>>;
  readonly recentDays: number;
  /** The "today" the recent window was computed against (YYYY-MM-DD). */
  readonly asOf: string;
}

/**
 * Per-category LLM-enrichment backlog: how many records exist, how many are already
 * enriched (unchanged since their stamp), and how many are pending — split into a
 * recent window (default 10 days, the session-report proposal horizon) and older.
 * Reads every record body (needed for the change-detection hash); ~400 small files
 * in a lived-in instance, a few ms — fine for a session-start call.
 */
export function enrichmentStatus(
  store: OntosStore,
  dataDir: string,
  opts: { recentDays?: number; today?: string; categories?: readonly EnrichableCategory[] } = {},
): EnrichmentStatus {
  const recentDays = opts.recentDays ?? 10;
  const today = opts.today ?? localToday();
  // "last N days" = N calendar dates INCLUDING today (today−(N−1) … today), so a
  // 10-day window covers exactly 10 dates, not 11 (Codex R1 #4).
  const cutoff = isoDaysBefore(today, Math.max(0, recentDays - 1));
  const categories: Partial<Record<EnrichableCategory, CategoryBacklog>> = {};
  for (const { path, category } of listEnrichableRecords(dataDir, opts.categories)) {
    let rec: RecordInput;
    let fm: Record<string, unknown>;
    try {
      // Bound the per-file cost: a pathological multi-MB "record" is skipped rather
      // than read — this probe runs at session start and must stay subsecond even
      // on a corpus that violates the small-files assumption (Codex R1 #6).
      if (statSync(path).size > 512 * 1024) continue;
      const parsed = parseFrontmatter(readFileSync(path, "utf8"));
      fm = parsed.frontmatter as Record<string, unknown>;
      rec = { path, category, frontmatter: fm, body: parsed.body } as unknown as RecordInput;
    } catch {
      continue; // unreadable — skip rather than crash a status probe
    }
    const bucket = (categories[category] ??= { total: 0, enriched: 0, pending: 0, pendingRecent: 0, pendingOlder: 0 });
    bucket.total++;
    if (!needsEnrichment(store, rec)) {
      bucket.enriched++;
      continue;
    }
    bucket.pending++;
    const d = recordDate(path, fm);
    if (d && d >= cutoff) bucket.pendingRecent++;
    else bucket.pendingOlder++;
  }
  return { categories, recentDays, asOf: today };
}

/** "YYYY-MM-DD" minus N days (UTC arithmetic — day precision is all we need). */
function isoDaysBefore(todayIso: string, days: number): string {
  const t = Date.parse(`${todayIso}T00:00:00Z`);
  if (!Number.isFinite(t)) return todayIso;
  return new Date(t - days * 86400000).toISOString().slice(0, 10);
}

export interface ConversationBacklog {
  total: number;
  /** Transcripts with a completed extraction stamp (id-level — a changed transcript still counts). */
  extracted: number;
  pending: number;
}

/**
 * Conversation-mining backlog at ID level: normalized transcripts on disk vs
 * completed extraction stamps. Deliberately does NOT hash transcript contents
 * (that would read megabytes at session start); a mined-then-grown transcript
 * counts as extracted here — the mining scan itself re-detects it by hash.
 */
export function conversationBacklog(
  store: OntosStore,
  dataDir: string,
  opts: { archiveDir?: string } = {},
): ConversationBacklog {
  const root = opts.archiveDir ?? join(dataDir, "_session-archive", "normalized");
  // Ids are session UUIDs (globally unique by construction across the year/month/host
  // tree); a Set still dedupes defensively so a stray duplicate can't skew the math.
  const ids = new Set<string>();
  const walk = (dir: string): void => {
    let entries;
    try {
      entries = readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const e of entries) {
      if (e.isDirectory()) walk(join(dir, e.name));
      else if (e.isFile() && e.name.endsWith(".jsonl")) ids.add(e.name.slice(0, -".jsonl".length));
    }
  };
  walk(root);
  const done = new Set(store.extractedTranscriptIds());
  let extracted = 0;
  for (const id of ids) if (done.has(id)) extracted++;
  return { total: ids.size, extracted, pending: ids.size - extracted };
}

// ── extraction-run marker ────────────────────────────────────────────────────

export interface ExtractionRun {
  /** What the batch is mining. "records" = mixed curated-record enrichment. */
  readonly kind: "worklog" | "conversation" | "records";
  /** Driver pid — a dead pid invalidates the marker regardless of freshness. */
  readonly pid?: number;
  readonly total?: number;
  readonly done?: number;
  /** Active provider/model selected by an agent-mediated driver. */
  readonly backend?: "claude" | "codex" | "source-host";
  readonly model?: string;
  readonly modelPolicy?: "economy";
  readonly startedAt?: string;
  /** Heartbeat — writers MUST bump this as items complete. ISO timestamp. */
  readonly updatedAt: string;
  readonly note?: string;
}

/** Default freshness window: a marker whose heartbeat is older than this is treated as dead. */
export const EXTRACTION_RUN_STALE_MS = 10 * 60 * 1000;

export function extractionRunPath(dataDir: string): string {
  return join(dataDir, "_indexes", "ontos-extract-run.json");
}

/** Write/refresh the run marker (atomic tmp+rename so readers never see a torn file). */
export function writeExtractionRun(dataDir: string, run: ExtractionRun): void {
  const path = extractionRunPath(dataDir);
  mkdirSync(join(dataDir, "_indexes"), { recursive: true });
  const tmp = `${path}.tmp-${process.pid}`;
  writeFileSync(tmp, JSON.stringify(run, null, 2));
  renameSync(tmp, path);
}

/** Remove the run marker (best-effort — a missing file is already the goal state). */
export function clearExtractionRun(dataDir: string): void {
  try {
    rmSync(extractionRunPath(dataDir), { force: true });
  } catch {
    /* ignore */
  }
}

/**
 * The current extraction run, or null when none should be considered live:
 * absent/malformed file, stale heartbeat (staleMs, default 10 min), or a pid
 * that provably no longer exists. Fail-open to "not running" — a wedged marker
 * must never permanently suppress proposals or disable dashboard buttons.
 */
export function readExtractionRun(
  dataDir: string,
  opts: { staleMs?: number; now?: number } = {},
): ExtractionRun | null {
  const path = extractionRunPath(dataDir);
  if (!existsSync(path)) return null;
  let run: ExtractionRun;
  try {
    run = JSON.parse(readFileSync(path, "utf8")) as ExtractionRun;
  } catch {
    return null;
  }
  if (!run || typeof run !== "object" || typeof run.updatedAt !== "string") return null;
  if (run.kind !== "worklog" && run.kind !== "conversation" && run.kind !== "records") return null;
  const now = opts.now ?? Date.now();
  const beat = Date.parse(run.updatedAt);
  if (!Number.isFinite(beat) || now - beat > (opts.staleMs ?? EXTRACTION_RUN_STALE_MS)) return null;
  // A FUTURE-dated heartbeat is a corrupt/clock-skewed file, not a live run — without
  // this it would evade the staleness check forever and permanently suppress proposals
  // (Codex R1 #2). Small skew tolerance for cross-process clock drift.
  if (beat - now > 2 * 60 * 1000) return null;
  if (typeof run.pid === "number" && run.pid > 0) {
    try {
      process.kill(run.pid, 0); // signal 0 = existence probe
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ESRCH") return null; // provably dead
      /* EPERM etc. — process exists but isn't ours; treat as alive */
    }
  }
  return run;
}
